<template>
  <main class="plan-page">
    <section class="plan-shell">
      <nav class="tabs"><button class="active">치료 계획 상담</button></nav>

      <div class="toolbar">
        <label class="patient-picker">
          <span>환자</span>
          <input v-model="patientQuery" placeholder="이름/차트/전화 검색" @focus="patientListOpen = true" />
          <div v-if="patientListOpen && patientQuery.trim()" class="patient-results">
            <button v-for="patient in filteredPatients" :key="patient.chartNo" type="button" @click="selectPatient(patient)">
              <strong>{{ patient.name }}</strong>
              <span>{{ patient.chartNo }} · {{ patient.phone || '-' }}</span>
            </button>
          </div>
        </label>
        <button @click="goPricing">치료비 계획 작성</button>
        <button :disabled="!selectedPlan" @click="markConfirmed">진행 확정</button>
        <button :disabled="!selectedPlan" @click="markCompleted">상담 완료</button>
      </div>

      <section class="summary-grid">
        <article><span>환자</span><strong>{{ currentPatient?.name || '미선택' }}</strong></article>
        <article><span>치료비 계획</span><strong>{{ patientPlans.length }}건</strong></article>
        <article><span>제출 계획</span><strong>{{ submittedCount }}건</strong></article>
        <article><span>선택 총액</span><strong>{{ money(selectedPlan?.total || 0) }}</strong></article>
      </section>

      <section class="content-grid">
        <aside class="plan-list">
          <header>환자별 치료계획</header>
          <button
            v-for="plan in patientPlans"
            :key="plan.id"
            type="button"
            :class="{ selected: selectedPlanId === plan.id }"
            @click="selectedPlanId = plan.id"
          >
            <strong>{{ plan.title }}</strong>
            <span>{{ plan.date }} · {{ plan.status }} · {{ money(plan.total) }}</span>
          </button>
          <p v-if="!patientPlans.length">가격 책정에서 제출된 계획이 없습니다.</p>
        </aside>

        <section class="detail-panel">
          <header>
            <strong>{{ selectedPlan?.title || '치료계획 선택' }}</strong>
            <span>{{ selectedPlan?.doctor || '-' }} · {{ selectedPlan?.counselor || '-' }}</span>
          </header>

          <div class="timeline">
            <article v-for="event in timeline" :key="event.id" :class="event.type">
              <span>{{ event.date }}</span>
              <strong>{{ event.title }}</strong>
              <p>{{ event.detail }}</p>
            </article>
          </div>

          <div class="table-area">
            <table>
              <thead><tr><th>치식/부위</th><th>치료계획</th><th>금액</th><th>메모</th></tr></thead>
              <tbody>
                <tr v-for="item in selectedPlan?.items || []" :key="item.id">
                  <td>{{ item.tooth || '-' }}</td>
                  <td>{{ item.name }}</td>
                  <td>{{ money(item.price) }}</td>
                  <td>{{ item.memo || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <footer>
            <label>
              <span>상담 메모</span>
              <textarea v-model="consultMemo" rows="4" placeholder="상담 결과, 보류 사유, 다음 상담 예정 등을 기록" />
            </label>
            <button :disabled="!selectedPlan" @click="saveMemo">메모 저장</button>
          </footer>
        </section>
      </section>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

type Patient = { id?: number; chartNo: string; name: string; phone?: string };
type PlanItem = { id: number; tooth: string; name: string; price: number; memo: string };
type TreatmentPlan = { id: number; chartNo: string; patientName: string; date: string; title: string; items: PlanItem[]; subtotal: number; vat: number; total: number; doctor: string; counselor: string; memo: string; status: string; consultMemo?: string; consultStatus?: string };

const STORAGE_KEY = 'bioa-treatment-plans';
const route = useRoute();
const router = useRouter();
const patients = ref<Patient[]>(loadPatients());
const plans = ref<TreatmentPlan[]>(readStorage<TreatmentPlan[]>(STORAGE_KEY, []));
const queryChartNo = String(route.query.chartNo || '');
const queryPlanId = Number(route.query.planId || 0);
const currentPatient = ref<Patient | null>(patients.value.find((p) => p.chartNo === queryChartNo) || patients.value[0] || null);
const patientQuery = ref(currentPatient.value ? `${currentPatient.value.name} ${currentPatient.value.chartNo}` : '');
const patientListOpen = ref(false);
const selectedPlanId = ref<number | null>(queryPlanId || null);
const consultMemo = ref('');

const filteredPatients = computed(() => {
  const q = patientQuery.value.trim().toLowerCase();
  return patients.value.filter((p) => [p.name, p.chartNo, p.phone].join(' ').toLowerCase().includes(q)).slice(0, 40);
});
const patientPlans = computed(() => plans.value.filter((plan) => !currentPatient.value || plan.chartNo === currentPatient.value.chartNo).sort((a, b) => b.id - a.id));
const selectedPlan = computed(() => patientPlans.value.find((plan) => plan.id === selectedPlanId.value) || patientPlans.value[0] || null);
const submittedCount = computed(() => patientPlans.value.filter((plan) => plan.status === '제출').length);
const timeline = computed(() => {
  const plan = selectedPlan.value;
  if (!plan) return [];
  return [
    { id: 'created', date: plan.date, title: '치료비 계획 작성', detail: `${plan.items.length}개 항목 · ${money(plan.total)}`, type: 'created' },
    { id: 'submitted', date: plan.date, title: `상태 ${plan.status}`, detail: plan.memo || '상담 대기', type: plan.status === '제출' ? 'submitted' : 'created' },
    { id: 'consult', date: today(), title: plan.consultStatus || '상담 진행', detail: plan.consultMemo || consultMemo.value || '상담 내용을 입력하세요.', type: 'consult' },
  ];
});

watch(selectedPlan, (plan) => {
  if (plan) {
    selectedPlanId.value = plan.id;
    consultMemo.value = plan.consultMemo || '';
  }
}, { immediate: true });

function selectPatient(patient: Patient) {
  currentPatient.value = patient;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
  selectedPlanId.value = null;
}

function goPricing() {
  router.push('/price');
}

function markConfirmed() {
  updateSelected({ status: '진행확정', consultStatus: '진행 확정' });
}

function markCompleted() {
  updateSelected({ consultStatus: '상담 완료' });
}

function saveMemo() {
  updateSelected({ consultMemo: consultMemo.value, consultStatus: '상담 기록' });
}

function updateSelected(patch: Partial<TreatmentPlan>) {
  const plan = selectedPlan.value;
  if (!plan) return;
  const index = plans.value.findIndex((item) => item.id === plan.id);
  if (index < 0) return;
  plans.value[index] = { ...plans.value[index], ...patch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans.value));
}

function money(value: number) {
  return `${Math.round(value || 0).toLocaleString('ko-KR')}원`;
}

function loadPatients(): Patient[] {
  const stored = readStorage<Patient[]>('bioa-patients', []);
  if (stored.length) return stored.map((p) => ({ chartNo: p.chartNo || `P-${p.id || 'TEMP'}`, name: p.name || '환자', phone: p.phone || '' }));
  return [];
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
</script>

<style scoped>
.plan-page{height:100%;min-height:0;overflow:hidden;background:#f6f9fb;color:#34404a;font-size:13px}.plan-shell{height:100%;min-height:0;background:#fff;border-top:1px solid #d9e2ea;display:flex;flex-direction:column;overflow:hidden}.tabs{display:flex;gap:22px;height:38px;align-items:flex-end;padding:0 20px;border-bottom:1px solid #d9e2ea;flex:0 0 auto}.tabs button{height:38px;border:0;background:transparent;color:#2275d7;font-weight:900;position:relative}.tabs button:after{content:'';position:absolute;left:8px;right:8px;bottom:-1px;height:2px;background:#2275d7}.toolbar{height:52px;display:flex;align-items:center;gap:6px;padding:8px 16px;border-bottom:1px solid #e3eaf0;box-sizing:border-box}.toolbar button,.detail-panel button{height:30px;border:0;border-radius:4px;background:#e8eef4;color:#3d4b57;padding:0 12px;font-weight:800;cursor:pointer}.toolbar button:disabled,.detail-panel button:disabled{opacity:.45}.patient-picker{position:relative;display:grid;grid-template-columns:auto 260px;align-items:center;gap:6px;color:#7b8994}.patient-picker input{height:30px;border:1px solid #cfd9e3;border-radius:4px;padding:0 8px}.patient-results{position:absolute;top:34px;left:36px;width:340px;max-height:240px;overflow:auto;border:1px solid #cfd9e3;background:#fff;box-shadow:0 14px 30px rgba(35,49,61,.16);z-index:5;padding:5px}.patient-results button{width:100%;height:auto;min-height:40px;background:#fff;text-align:left;display:grid;grid-template-columns:1fr auto}.summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;padding:12px 16px;border-bottom:1px solid #e3eaf0;background:#fbfcfd}.summary-grid article{border:1px solid #dce5ec;background:#fff;border-radius:4px;padding:10px}.summary-grid span{display:block;color:#7b8994}.summary-grid strong{display:block;margin-top:4px;font-size:17px;color:#263746}.content-grid{min-height:0;flex:1;display:grid;grid-template-columns:310px minmax(0,1fr);overflow:hidden}.plan-list{min-height:0;border-right:1px solid #e3eaf0;background:#fbfcfd;padding:12px;box-sizing:border-box;overflow:auto;display:grid;align-content:start;gap:7px}.plan-list header{font-weight:900;color:#263746;margin-bottom:4px}.plan-list button{height:auto;min-height:46px;border:1px solid #e3eaf0;border-radius:4px;background:#fff;text-align:left;display:grid;gap:3px;padding:8px;cursor:pointer}.plan-list button:hover,.plan-list button.selected{border-color:#2f7bea;background:#eef6ff}.plan-list span{color:#7b8994}.plan-list p{margin:0;padding:12px;color:#8a97a3}.detail-panel{min-height:0;display:flex;flex-direction:column;overflow:hidden}.detail-panel>header{height:48px;border-bottom:1px solid #e3eaf0;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 16px}.detail-panel>header strong{font-size:16px;color:#263746}.detail-panel>header span{color:#7b8994}.timeline{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;padding:12px 16px;border-bottom:1px solid #e3eaf0;background:#fbfcfd}.timeline article{border:1px solid #dce5ec;border-left:4px solid #cfd9e3;background:#fff;border-radius:4px;padding:10px}.timeline article.submitted{border-left-color:#2f7bea}.timeline article.consult{border-left-color:#22a06b}.timeline span{color:#7b8994}.timeline strong{display:block;margin-top:4px;color:#263746}.timeline p{margin:4px 0 0;color:#5f6e7b}.table-area{min-height:0;flex:1;overflow:auto}.table-area table{width:100%;border-collapse:collapse;table-layout:fixed}.table-area th,.table-area td{height:34px;border-bottom:1px solid #e5ebf0;border-right:1px solid #edf2f6;padding:0 8px;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.table-area th{position:sticky;top:0;background:#f8fafc;color:#7b8994}.detail-panel footer{border-top:1px solid #d9e2ea;padding:12px 16px;display:grid;grid-template-columns:minmax(0,1fr) 96px;gap:10px;align-items:end}.detail-panel footer label{display:grid;gap:6px;color:#5f6e7b}.detail-panel textarea{width:100%;border:1px solid #cfd9e3;border-radius:4px;padding:8px;box-sizing:border-box;resize:vertical}
</style>
